import {
  getLanguageSettingsValidationError,
  type Dictionary,
  type Language,
} from "@/domain/dictionary";
import {
  DEFAULT_EXPERIMENTAL_FEATURES,
  normalizeExperimentalFeatures,
  type ExperimentalFeatures,
} from "@/domain/experimentalFeatures";
import { getAgeGroupsValidationError, getArenasValidationError } from "@/domain/settings";
import type { AgeGroup, Arena, View } from "@/domain/types";
import { DEFAULT_VIEWS, getViewSettingsValidationError, sortViews } from "@/domain/views";
import { getSupabase, unwrap } from "./supabaseClient";
import { normalizeDefaultUrlParams, type DefaultUrlParams } from "@/domain/defaultUrlParams";

/** Administrator-maintained configuration stored in the single `matrix_settings` row. */
export interface MatrixSettings {
  ageGroups: AgeGroup[];
  arenas: Arena[];
  views: View[];
  languages: Language[];
  dictionary: Dictionary;
  experimentalFeatures: ExperimentalFeatures;
  defaultUrlParams: DefaultUrlParams;
}

export type SettingsSection = "ageGroups" | "arenas" | "views" | "languages";

/** Raised when the stored settings fail validation; `section` drives the message. */
export class InvalidSettingsError extends Error {
  constructor(
    readonly section: SettingsSection,
    readonly code: string,
  ) {
    super(`${section}: ${code}`);
  }
}

interface MatrixSettingsRow {
  age_groups: unknown;
  arenas: unknown;
  views: unknown;
  languages: unknown;
  dictionary: unknown;
  default_url_params?: unknown;
}

export async function fetchMatrixSettings(): Promise<MatrixSettings> {
  const experimentalFeatures = fetchExperimentalFeatures();
  const row = unwrap(
    await getSupabase()
      .from("matrix_settings")
      .select("age_groups, arenas, views, languages, dictionary, default_url_params")
      .eq("id", true)
      .single(),
  ) as MatrixSettingsRow;

  const ageGroupsError = getAgeGroupsValidationError(row.age_groups);
  if (ageGroupsError) throw new InvalidSettingsError("ageGroups", ageGroupsError);
  const arenasError = getArenasValidationError(row.arenas);
  if (arenasError) throw new InvalidSettingsError("arenas", arenasError);
  // Views are optional until an administrator saves them for the first time.
  const views = (row.views ?? DEFAULT_VIEWS) as View[];
  const viewsError = getViewSettingsValidationError(views);
  if (viewsError) throw new InvalidSettingsError("views", viewsError);
  const languagesError = getLanguageSettingsValidationError(row.languages, row.dictionary);
  if (languagesError) throw new InvalidSettingsError("languages", languagesError);

  return {
    ageGroups: row.age_groups as AgeGroup[],
    arenas: row.arenas as Arena[],
    views: sortViews(views),
    languages: row.languages as Language[],
    dictionary: row.dictionary as Dictionary,
    experimentalFeatures: await experimentalFeatures,
    defaultUrlParams: normalizeDefaultUrlParams(row.default_url_params),
  };
}

/**
 * Read separately so a database without the `experimental_features` column
 * (schema.sql section 11 not yet run) still loads, with every feature off.
 */
async function fetchExperimentalFeatures(): Promise<ExperimentalFeatures> {
  const { data, error } = await getSupabase()
    .from("matrix_settings")
    .select("experimental_features")
    .eq("id", true)
    .maybeSingle();
  if (error) {
    console.warn("Could not load experimental features:", error.message);
    return DEFAULT_EXPERIMENTAL_FEATURES;
  }
  return normalizeExperimentalFeatures(
    (data as { experimental_features?: unknown } | null)?.experimental_features,
  );
}
